import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface QueueItem {
  id: string
  content_type: string
  content: string
  status: string
  reported_by: string
  reason: string
  created_at: string
  priority: string
}

interface Rule {
  id: string
  name: string
  type: string
  pattern: string
  action: string
  enabled: boolean
  triggered_count: number
}

export default function ModerationPage() {
  const [queue, setQueue] = useState<QueueItem[]>([])
  const [rules, setRules] = useState<Rule[]>([])
  const [actionsLog, setActionsLog] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<'queue' | 'rules' | 'log'>('queue')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [queueRes, rulesRes, logRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/moderation/queue'),
      fetch(API_BASE + '/moderation/rules'),
      fetch(API_BASE + '/moderation/actions'),
      fetch(API_BASE + '/moderation/statistics')
    ])
    if (queueRes.ok) setQueue((await queueRes.json()).queue || [])
    if (rulesRes.ok) setRules((await rulesRes.json()).rules || [])
    if (logRes.ok) setActionsLog((await logRes.json()).actions || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const moderateContent = async (id: string, action: string) => {
    await fetch(API_BASE + '/moderation/queue/' + id + '/moderate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, notes: '' })
    })
    fetchData()
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'low': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Content Moderation</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_items}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Processed Today</div>
              <div className="text-2xl font-bold">{statistics.processed_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Auto Filtered</div>
              <div className="text-2xl font-bold">{statistics.auto_filtered}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Manual Reviews</div>
              <div className="text-2xl font-bold">{statistics.manual_reviews}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Approval Rate</div>
              <div className="text-2xl font-bold text-green-600">{statistics.approval_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Review Time</div>
              <div className="text-2xl font-bold">{statistics.avg_review_time_min} min</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button onClick={() => setActiveTab('queue')} className={`px-4 py-2 rounded-lg ${activeTab === 'queue' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Moderation Queue</button>
          <button onClick={() => setActiveTab('rules')} className={`px-4 py-2 rounded-lg ${activeTab === 'rules' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Rules</button>
          <button onClick={() => setActiveTab('log')} className={`px-4 py-2 rounded-lg ${activeTab === 'log' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>Actions Log</button>
        </div>

        {activeTab === 'queue' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Pending Review</h2></div>
            <div className="divide-y">
              {queue.filter(q => q.status === 'pending').map(item => (
                <div key={item.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs ${getPriorityColor(item.priority)}`}>{item.priority}</span>
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs">{item.content_type}</span>
                      <span className="text-sm text-gray-500">Reason: {item.reason}</span>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => moderateContent(item.id, 'approved')} className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700">Approve</button>
                      <button onClick={() => moderateContent(item.id, 'rejected')} className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700">Reject</button>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 rounded text-sm">{item.content}</div>
                  <div className="mt-2 text-xs text-gray-400">Reported by: {item.reported_by} | {new Date(item.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'rules' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">Moderation Rules</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Rule</button>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pattern</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Triggered</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rules.map(rule => (
                  <tr key={rule.id}>
                    <td className="px-6 py-4 font-medium">{rule.name}</td>
                    <td className="px-6 py-4"><span className="px-2 py-1 bg-gray-100 rounded text-xs">{rule.type}</span></td>
                    <td className="px-6 py-4 font-mono text-sm text-gray-600">{rule.pattern}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 rounded text-xs ${rule.action === 'reject' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{rule.action}</span></td>
                    <td className="px-6 py-4">{rule.triggered_count}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 rounded-full text-xs ${rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{rule.enabled ? 'Enabled' : 'Disabled'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'log' && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Actions Log</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Content ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Moderator</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {actionsLog.map(action => (
                  <tr key={action.id}>
                    <td className="px-6 py-4 font-mono text-sm">{action.content_id}</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 rounded text-xs ${action.action === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{action.action}</span></td>
                    <td className="px-6 py-4 text-sm">{action.moderator}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{action.notes || '-'}</td>
                    <td className="px-6 py-4 text-sm">{new Date(action.timestamp).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
